import type { ListSupportCasesResponse, SupportCaseListItem } from "@wallyo/contracts/console/cases";

/**
 * The queue as the screen holds it: the pages read so far, one after the
 * other. New filters (or a reload) replace everything; "Charger plus" appends
 * the next page. A reload keeps the rows on screen until the answer arrives,
 * and a failed "Charger plus" keeps what is already shown.
 */
export type QueueState = {
  status: "loading" | "ready" | "error";
  cases: readonly SupportCaseListItem[];
  nextCursor: string | null;
  error: unknown;
  loadingMore: boolean;
  moreError: unknown;
};

export type QueueEvent =
  | { type: "load"; keep: boolean }
  | { type: "loaded"; page: ListSupportCasesResponse }
  | { type: "failed"; error: unknown }
  | { type: "loadMore" }
  | { type: "loadedMore"; page: ListSupportCasesResponse }
  | { type: "moreFailed"; error: unknown };

export const INITIAL_QUEUE_STATE: QueueState = {
  status: "loading",
  cases: [],
  nextCursor: null,
  error: null,
  loadingMore: false,
  moreError: null,
};

export function queueReducer(state: QueueState, event: QueueEvent): QueueState {
  switch (event.type) {
    case "load":
      return event.keep ? { ...state, error: null } : INITIAL_QUEUE_STATE;
    case "loaded":
      return { ...INITIAL_QUEUE_STATE, status: "ready", cases: event.page.cases, nextCursor: event.page.nextCursor };
    case "failed":
      return { ...state, status: "error", error: event.error, loadingMore: false };
    case "loadMore":
      return { ...state, loadingMore: true, moreError: null };
    case "loadedMore": {
      // A page read twice (a retried click) adds nothing it already has.
      const seen = new Set(state.cases.map((item) => item.supportCaseId));
      const fresh = event.page.cases.filter((item) => !seen.has(item.supportCaseId));
      return {
        ...state,
        cases: [...state.cases, ...fresh],
        nextCursor: event.page.nextCursor,
        loadingMore: false,
        moreError: null,
      };
    }
    case "moreFailed":
      return { ...state, loadingMore: false, moreError: event.error };
  }
}
